import { SoundButton } from "../../../../common/SoundButton/SoundButton";
import type {
    ApiFriendStealCryptoEligibilityV2,
    ApiFriendStealGrant,
    ApiFriendStealResV2,
    ApiFriendStealTargetV2,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import beachCoinStyle1Icon from "../../../../../assets/static/images/coins/style1/coin-style1__beach-coin.webp";
import gramStyle1Icon from "../../../../../assets/static/images/coins/style1/coin-style1__gram.webp";
import notcoinStyle1Icon from "../../../../../assets/static/images/coins/style1/coin-style1__not.webp";
import usdtStyle1Icon from "../../../../../assets/static/images/coins/style1/coin-style1__usdt.webp";
import jukoJuniorAvatar from "../../../../../assets/static/images/friend-steal/dummy/pb-dummy-user__juko-junior.webp";
import lucaModricAvatar from "../../../../../assets/static/images/friend-steal/dummy/pb-dummy-user__luca-modric.webp";
import friendIcon from "../../../../../assets/static/images/friend-steal/friend-icon.png";
import friendStealIcon from "../../../../../assets/static/images/symbols/v1/symbol-friend-steal.webp";
import FriendStealRedArrowRightIcon from "../../../../../assets/static/icons/friend-steal__red-arrow-right-icon.svg?react";
import ShineReflectionDot from "../../../../../assets/static/icons/shine-reflection-dot.svg?react";
import sfxCoinsFalling from "../../../../../assets/static/audio/slot-machine-coins-falling.mp3";
import { playSound, preloadSounds } from "../../../../../logic/helpers/audio";
import { formatCompactCurrencyMinorUnits } from "../../../../../logic/helpers/currencyAmount";
import { haptics } from "../../../../../logic/helpers/haptics";
import { getTelegramWebApp } from "../../../../../logic/helpers/telegram";
import { useFriendSteals } from "../../../../../logic/hooks/useFriendSteals";
import { useFriendInviteSharing } from "../../../../../logic/hooks/useSocialFriendInvites";
import { resolveSupportedLocale } from "../../../../../logic/i18n/locales";
import {
    ButtonVar1,
    type ButtonVar1Colors,
} from "../../../../common/ButtonVar1/ButtonVar1";
import { SimpleDialog } from "../../../../common/SimpleDialog/SimpleDialog";
import "./FriendStealDialog.scss";
import Green3DPlusIcon from "../../../../../assets/static/images/friend-steal/dummy/green-3d-plus.png";

const AVAILABLE_TARGET_COLORS: ButtonVar1Colors = {
    faceFill: `linear-gradient(
        to bottom,
        #fbe26e 0%,
        #fbe26e 3%,
        #fee25d 7%,
        #fde15a 11%,
        #ffe15b 15%,
        #ffe35c 26%,
        #ffe35e 31%,
        #ffe15b 38%,
        #ffe35c 45%,
        #ffe04f 51%,
        #f8ce2e 52%,
        #fdce1c 55%,
        #ffce1c 58%,
        #feca1e 65%,
        #fdc41b 73%,
        #ffc51b 80%,
        #fdc21e 85%,
        #ffbe1a 89%,
        #fdbe19 93%,
        #fdbf1e 96%,
        #f9c124 100%
    )`,
    faceStroke: `linear-gradient(
        to bottom,
        #fdefa6 0%,
        #fdf3ab 3%,
        #feef9c 7%,
        #fdf1a3 10%,
        #fdf3ac 14%,
        #fcf3ae 18%,
        #fef3a5 23%,
        #fcf1a2 28%,
        #fcf4a3 33%,
        #fdf4a7 37%,
        #fff6a6 40%,
        #fcf4a1 45%,
        #fef6a3 49%,
        #fff292 51%,
        #faeb76 51%,
        #feed6b 54%,
        #feea67 60%,
        #feeb73 65%,
        #feeb72 70%,
        #feea71 74%,
        #feea6f 80%,
        #fce968 85%,
        #ffe769 89%,
        #fce767 93%,
        #ffe35c 97%,
        #ffe56a 100%
    )`,
    containerFill: `linear-gradient(
        to bottom,
        #a17224 0%,
        #a17224 83%,
        #b5751b 87%,
        #bf791d 91%,
        #c07719 94%,
        #c37b19 97%,
        #ba7925 100%
    )`,
};

interface DummyFriendTarget {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl: string;
}

const DUMMY_FRIEND_TARGETS: readonly DummyFriendTarget[] = [
    {
        id: "dummy-juko-junior-1",
        firstName: "Juko",
        lastName: "Junior",
        avatarUrl: jukoJuniorAvatar,
    },
    {
        id: "dummy-luca-modric",
        firstName: "Luca",
        lastName: "Modric",
        avatarUrl: lucaModricAvatar,
    },
    {
        id: "dummy-juko-junior-2",
        firstName: "Juko",
        lastName: "Junior",
        avatarUrl: jukoJuniorAvatar,
    },
];

const ADD_FRIEND_COLORS: ButtonVar1Colors = {
    faceFill: `linear-gradient(
        to bottom,
        #c4d4e6 0%,
        #d0deee 4%,
        #d1def0 7%,
        #d1dfee 14%,
        #d1dfef 24%,
        #d2e0ee 33%,
        #d3dfee 46%,
        #d2dfee 56%,
        #d1dfef 70%,
        #d0deee 83%,
        #d0dfef 90%,
        #d1deee 96%,
        #cfdeef 100%
    )`,
    faceStroke: `linear-gradient(
        to bottom,
        #e8edf4 0%,
        #eef3f8 6%,
        #ecf2f8 11%,
        #ecf3f6 17%,
        #eef4f9 23%,
        #eef3f7 31%,
        #eef3f8 39%,
        #eff4f9 47%,
        #eef4f8 53%,
        #edf4f8 62%,
        #eef4f9 70%,
        #ecf3f7 77%,
        #eef3f9 84%,
        #eef3f7 90%,
        #edf2f8 95%,
        #ecf2f8 100%
    )`,
    containerFill: `linear-gradient(
        to bottom,
        #a9bed5 0%,
        #a9bfd5 83%,
        #a9c0d9 87%,
        #aac1da 91%,
        #a8c1db 94%,
        #a6c0db 97%,
        #aac2dc 100%
    )`,
    containerStroke: "#99afc6",
};

const ALL_TARGETS_UNPICKABLE_ADD_FRIEND_COLORS: ButtonVar1Colors = {
    ...ADD_FRIEND_COLORS,
    faceStroke: `linear-gradient(
        to bottom,
        #9EFF8B 0%,
        #A6FE90 8%,
        #90FC82 17%,
        #94FB8E 26%,
        #84F887 39%,
        #72F38C 52%,
        #54EF7A 63%,
        #4AEA7C 74%,
        #33EC6D 84%,
        #35ED71 92%,
        #2FEE6D 100%
    )`,
    faceFill: `linear-gradient(
        to bottom,
        #75E964 0%,
        #75E964 4%,
        #52FF43 12%,
        #6EF46C 22%,
        #5EF168 32%,
        #63EB76 42%,
        #37E258 52%,
        #34D65E 60%,
        #11D44F 68%,
        #09D54E 79%,
        #09D44E 85%,
        #02C349 94%,
        #14C956 100%
    )`,
    containerFill: `linear-gradient(
        to bottom,
        #0C7339 0%,
        #0C7339 79%,
        #157C47 86%,
        #157B4B 95%,
        #137446 100%
    )`,
    containerStroke: "#0D652D",
};

const FRIENDLESS_ADD_FRIEND_COLORS: ButtonVar1Colors = {
    faceFill: `linear-gradient(
        to bottom,
        #00DC67 0%,
        #00DC67 100%
    )`,
    faceStroke: `linear-gradient(
        to bottom,
        #00883F 0%,
        #00883F 100%
    )`,
    containerFill: `linear-gradient(
        to bottom,
        #00883F 0%,
        #00883F 100%
    )`,
    containerStroke: "#005829",
};

type FriendStealSuccess = Extract<ApiFriendStealResV2, { status: "OK" }>;

interface CompletedSteal {
    target: ApiFriendStealTargetV2;
    response: FriendStealSuccess;
}

interface FriendStealDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

interface GrantTimer {
    grantId: ApiFriendStealGrant["grantId"] | null;
    expiresAt: ApiFriendStealGrant["expiresAt"];
    countdown: string;
}

interface HorizontalOverflow {
    left: boolean;
    right: boolean;
}

const STYLE_1_CURRENCY_ICONS: Readonly<Record<string, string>> = {
    BEACH_COINS: beachCoinStyle1Icon,
    GRAM: gramStyle1Icon,
    NOT: notcoinStyle1Icon,
    USDT: usdtStyle1Icon,
};

const CLAIM_TRANSFER_SCALE_MS = 220;
const CLAIM_TRANSFER_HOLD_MS = 750;
const REDUCED_MOTION_TRANSFER_SCALE_MS = 20;
const REDUCED_MOTION_TRANSFER_HOLD_MS = 80;
const FRIEND_STEAL_SUCCESS_TOAST_ID = "friend-steal-success";
const FRIEND_STEAL_SUCCESS_TOAST_DURATION_MS = 3_000;

function targetCryptoEligibilityKey(
    eligibility: ApiFriendStealCryptoEligibilityV2,
): string | null {
    switch (eligibility) {
        case "ELIGIBLE":
            return null;
        case "ACTOR_VERIFICATION_REQUIRED":
            return "friendSteal.dialog.cryptoEligibility.actorVerificationRequired";
        case "TARGET_VERIFICATION_REQUIRED":
            return "friendSteal.dialog.cryptoEligibility.targetVerificationRequired";
        case "BOTH_VERIFICATION_REQUIRED":
            return "friendSteal.dialog.cryptoEligibility.bothVerificationRequired";
        case "POLICY_RESTRICTED":
            return "friendSteal.dialog.cryptoEligibility.policyRestricted";
    }
}

function resultCryptoEligibilityKey(
    eligibility: ApiFriendStealCryptoEligibilityV2,
): string | null {
    if (eligibility === "ELIGIBLE") return null;
    return eligibility === "POLICY_RESTRICTED"
        ? "friendSteal.dialog.cryptoLimitedPolicy"
        : "friendSteal.dialog.cryptoLimitedVerification";
}

const FRIEND_STEAL_TITLE_ASSET_PREFIX =
    "../../../../../assets/static/images/friend-steal/friend-steal-title__";
const friendStealTitleAssets = import.meta.glob<string>(
    "../../../../../assets/static/images/friend-steal/friend-steal-title__*.webp",
    { eager: true, import: "default" },
);
const defaultFriendStealTitleAsset =
    friendStealTitleAssets[`${FRIEND_STEAL_TITLE_ASSET_PREFIX}en.webp`] ??
    (() => {
        throw new Error("Missing default localized friend-steal title asset");
    })();

function getFriendStealTitleAsset(locale: string): string {
    const assetLocale = resolveSupportedLocale(locale).toLowerCase();
    return (
        friendStealTitleAssets[
            `${FRIEND_STEAL_TITLE_ASSET_PREFIX}${assetLocale}.webp`
        ] ?? defaultFriendStealTitleAsset
    );
}

function displayInitials(
    displayName: string,
    firstName?: string | null,
    lastName?: string | null,
): string {
    const namedInitials = [firstName, lastName]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.slice(0, 1))
        .join("");
    if (namedInitials) return namedInitials.toUpperCase();

    return displayName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.slice(0, 1))
        .join("")
        .toUpperCase();
}

function countdownUntil(isoDate: string | null | undefined, now: number) {
    if (!isoDate) return null;
    const milliseconds = Date.parse(isoDate) - now;
    if (milliseconds <= 0) return null;
    const seconds = Math.ceil(milliseconds / 1_000);
    const hours = Math.floor(seconds / 3_600);
    const minutes = Math.floor((seconds % 3_600) / 60);
    const remainingSeconds = seconds % 60;
    return [hours, minutes, remainingSeconds]
        .map((part) => part.toString().padStart(2, "0"))
        .join(":");
}

function useCurrentTime(enabled: boolean) {
    const [now, setNow] = useState(Date.now);

    useEffect(() => {
        if (!enabled) return;
        setNow(Date.now());
        const interval = window.setInterval(() => setNow(Date.now()), 1_000);
        return () => window.clearInterval(interval);
    }, [enabled]);

    return now;
}

interface AvatarProps {
    avatarUrl: string | null | undefined;
    displayName: string;
    firstName?: string | null;
    lastName?: string | null;
    className?: string;
}

function Avatar({
    avatarUrl,
    displayName,
    firstName,
    lastName,
    className,
}: AvatarProps) {
    return (
        <span
            className={["friend-steal-dialog__avatar", className]
                .filter(Boolean)
                .join(" ")}
            aria-hidden="true"
        >
            <span className="friend-steal-dialog__avatar-initials">
                {displayInitials(displayName, firstName, lastName)}
            </span>
            {avatarUrl && (
                <img
                    className="friend-steal-dialog__avatar-photo"
                    src={avatarUrl}
                    alt=""
                    draggable={false}
                    loading="lazy"
                    onError={(event) => {
                        event.currentTarget.style.display = "none";
                    }}
                />
            )}
        </span>
    );
}

function CurrencyMark({ currencyCode }: { currencyCode: string }) {
    const icon = STYLE_1_CURRENCY_ICONS[currencyCode];
    if (icon) {
        return (
            <img
                className="friend-steal-dialog__currency"
                src={icon}
                alt=""
                aria-hidden="true"
                draggable={false}
            />
        );
    }

    return (
        <span
            className="friend-steal-dialog__currency friend-steal-dialog__currency--fallback"
            aria-hidden="true"
        >
            {currencyCode.slice(0, 2)}
        </span>
    );
}

/* -------------------------------------------------------------------------- */
/*                             FRIEND-STEAL-RESULT                            */
/* -------------------------------------------------------------------------- */

function waitForClaimStep(
    milliseconds: number,
    signal: AbortSignal,
): Promise<boolean> {
    return new Promise((resolve) => {
        if (signal.aborted) {
            resolve(false);
            return;
        }

        const handleAbort = () => {
            window.clearTimeout(timer);
            resolve(false);
        };
        const timer = window.setTimeout(() => {
            signal.removeEventListener("abort", handleAbort);
            resolve(true);
        }, milliseconds);
        signal.addEventListener("abort", handleAbort, { once: true });
    });
}

interface FriendStealResultDialogProps {
    completedSteal: CompletedSteal;
    locale: string;
    onClaimingChange: (isClaiming: boolean) => void;
    onClose: () => void;
}

function FriendStealResultDialog({
    completedSteal,
    locale,
    onClaimingChange,
    onClose,
}: FriendStealResultDialogProps) {
    const { t } = useTranslation();
    const [activeTransferIndex, setActiveTransferIndex] = useState<
        number | null
    >(null);
    const [isClaiming, setIsClaiming] = useState(false);
    const claimControllerRef = useRef<AbortController | null>(null);
    const { response, target } = completedSteal;
    const telegramUser = getTelegramWebApp()?.initDataUnsafe.user;
    const actorDisplayName =
        [telegramUser?.first_name, telegramUser?.last_name]
            .filter((value): value is string => Boolean(value))
            .join(" ") || t("friendSteal.dialog.you");
    const transferRows = Array.from(
        { length: Math.ceil(response.transfers.length / 2) },
        (_, rowIndex) =>
            response.transfers.slice(rowIndex * 2, rowIndex * 2 + 2),
    );
    const cryptoEligibilityMessageKey = resultCryptoEligibilityKey(
        response.cryptoEligibility,
    );

    useEffect(() => {
        preloadSounds([sfxCoinsFalling]);
        return () => {
            claimControllerRef.current?.abort();
            onClaimingChange(false);
        };
    }, [onClaimingChange]);

    const claimTransfers = async () => {
        if (claimControllerRef.current) return;

        haptics.impact("light");
        preloadSounds([sfxCoinsFalling]);
        const controller = new AbortController();
        claimControllerRef.current = controller;
        setIsClaiming(true);
        onClaimingChange(true);

        const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        const scaleDuration = prefersReducedMotion
            ? REDUCED_MOTION_TRANSFER_SCALE_MS
            : CLAIM_TRANSFER_SCALE_MS;
        const holdDuration = prefersReducedMotion
            ? REDUCED_MOTION_TRANSFER_HOLD_MS
            : CLAIM_TRANSFER_HOLD_MS;

        for (
            let transferIndex = 0;
            transferIndex < response.transfers.length;
            transferIndex += 1
        ) {
            setActiveTransferIndex(transferIndex);
            if (!(await waitForClaimStep(scaleDuration, controller.signal))) {
                return;
            }
            haptics.impact("light");
            playSound(sfxCoinsFalling);
            if (!(await waitForClaimStep(holdDuration, controller.signal))) {
                return;
            }
        }

        setActiveTransferIndex(null);
        if (!(await waitForClaimStep(scaleDuration, controller.signal))) {
            return;
        }

        await new Promise((resolve) => setTimeout(resolve, 250));

        haptics.impact("heavy");

        claimControllerRef.current = null;
        setIsClaiming(false);
        onClaimingChange(false);
        onClose();
        toast.success(t("friendSteal.toasts.success"), {
            id: FRIEND_STEAL_SUCCESS_TOAST_ID,
            duration: FRIEND_STEAL_SUCCESS_TOAST_DURATION_MS,
        });
    };

    return (
        <div className="friend-steal-dialog__result">
            <div
                className="friend-steal-dialog__result-visual"
                aria-label={t("friendSteal.dialog.transferDirection", {
                    name: target.displayName,
                })}
            >
                <Avatar
                    avatarUrl={target.avatarUrl}
                    displayName={target.displayName}
                    firstName={target.firstName}
                    lastName={target.lastName}
                    className="friend-steal-dialog__result-avatar friend-steal-dialog__result-avatar--victim"
                />
                <FriendStealRedArrowRightIcon
                    className="friend-steal-dialog__result-arrow"
                    aria-hidden="true"
                    focusable="false"
                />
                <Avatar
                    avatarUrl={telegramUser?.photo_url}
                    displayName={actorDisplayName}
                    firstName={telegramUser?.first_name}
                    lastName={telegramUser?.last_name}
                    className="friend-steal-dialog__result-avatar friend-steal-dialog__result-avatar--robber"
                />
            </div>

            <p className="friend-steal-dialog__result-title">
                <Trans
                    i18nKey="friendSteal.dialog.success"
                    values={{ name: target.displayName }}
                    components={{ highlight: <strong /> }}
                />
            </p>

            {response.transfers.length > 0 ? (
                <ul className="friend-steal-dialog__result-transfers">
                    {transferRows.map((row, rowIndex) => (
                        <li
                            className="friend-steal-dialog__result-transfer-row"
                            key={row
                                .map((transfer) => transfer.currencyCode)
                                .join("-")}
                        >
                            {row.map((transfer, columnIndex) => {
                                const displayAmount =
                                    formatCompactCurrencyMinorUnits(
                                        transfer.amount,
                                        transfer.currencyCode,
                                        locale,
                                    );
                                const transferIndex =
                                    rowIndex * 2 + columnIndex;
                                return (
                                    <span
                                        className="friend-steal-dialog__result-transfer"
                                        key={transfer.currencyCode}
                                        data-claim-state={
                                            activeTransferIndex ===
                                            transferIndex
                                                ? "active"
                                                : "idle"
                                        }
                                        aria-label={t(
                                            "friendSteal.dialog.currencyTransfer",
                                            {
                                                currency: transfer.currencyCode,
                                                amount: displayAmount,
                                            },
                                        )}
                                        dir="ltr"
                                    >
                                        <CurrencyMark
                                            currencyCode={transfer.currencyCode}
                                        />
                                        <span
                                            className="friend-steal-dialog__result-transfer-amount"
                                            aria-hidden="true"
                                        >
                                            −{displayAmount}
                                        </span>
                                    </span>
                                );
                            })}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="friend-steal-dialog__result-empty">
                    {t("friendSteal.dialog.noTransfers")}
                </p>
            )}

            {cryptoEligibilityMessageKey && (
                <p
                    className="friend-steal-dialog__result-crypto-limit"
                    role="status"
                >
                    {t(cryptoEligibilityMessageKey)}
                </p>
            )}

            <ButtonVar1
                className="friend-steal-dialog__result-claim"
                clickSound="none"
                text={isClaiming ? undefined : t("friendSteal.dialog.claim")}
                content={
                    isClaiming ? (
                        <span
                            className="friend-steal-dialog__result-claim-spinner"
                            aria-hidden="true"
                        />
                    ) : undefined
                }
                disabled={isClaiming}
                aria-busy={isClaiming}
                aria-label={t(
                    isClaiming
                        ? "friendSteal.dialog.claiming"
                        : "friendSteal.dialog.claim",
                )}
                onClick={() => void claimTransfers()}
            />
        </div>
    );
}

function EmptyListArtwork() {
    return (
        <div className="friend-steal-dialog__skeleton" aria-hidden="true">
            {[0, 1, 2].map((row) => (
                <span className="friend-steal-dialog__skeleton-row" key={row}>
                    <span className="friend-steal-dialog__skeleton-avatar" />
                    <span className="friend-steal-dialog__skeleton-name" />
                    <span className="friend-steal-dialog__skeleton-action" />
                </span>
            ))}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*                                    MAIN                                    */
/* -------------------------------------------------------------------------- */

export function FriendStealDialog({ isOpen, onClose }: FriendStealDialogProps) {
    const { t, i18n } = useTranslation();
    const { state: authState } = useSlice("authSlice");
    const isAuthReady = authState.bootstrapStatus === "ready";
    // Target selection is intentionally fetched only while this dialog is
    // open; the slot screen does not pay for this eventual read on startup.
    const friendSteals = useFriendSteals(isOpen && isAuthReady);
    const friendInviteSharing = useFriendInviteSharing({
        enabled: isOpen && isAuthReady,
        userId: authState.userId,
        // Change this back to "prepared-message" when restoring the legacy
        // addFriend handlers commented beside the two buttons below.
        preferredTransport: "share-link",
    });
    const [completedSteal, setCompletedSteal] = useState<CompletedSteal | null>(
        null,
    );
    const [actionError, setActionError] = useState<string | null>(null);
    const [isClaimAnimationRunning, setIsClaimAnimationRunning] =
        useState(false);
    const refreshedExpiryRef = useRef<string | null>(null);
    const inventoryGrantsRef = useRef<HTMLDivElement | null>(null);
    const [inventoryOverflow, setInventoryOverflow] =
        useState<HorizontalOverflow>({
            left: false,
            right: false,
        });
    const now = useCurrentTime(isOpen);
    const inventory = friendSteals.inventory;
    const availableGrantCount = inventory?.availableGrantCount ?? 0;
    const inventoryTitleKey =
        availableGrantCount === 0
            ? "friendSteal.dialog.inventoryTitleZero"
            : availableGrantCount === 1
              ? "friendSteal.dialog.inventoryTitleOnce"
              : availableGrantCount === 2
                ? "friendSteal.dialog.inventoryTitleTwice"
                : "friendSteal.dialog.inventoryTitleCount";
    const nextGrantCountdown = countdownUntil(
        inventory?.nextGrantExpiresAt,
        now,
    );
    const inventoryGrantTimers: GrantTimer[] = friendSteals.grants.flatMap(
        (grant) => {
            const countdown = countdownUntil(grant.expiresAt, now);
            return countdown
                ? [
                      {
                          grantId: grant.grantId,
                          expiresAt: grant.expiresAt,
                          countdown,
                      },
                  ]
                : [];
        },
    );
    const grantTimers: GrantTimer[] =
        inventoryGrantTimers.length > 0 ||
        friendSteals.loadStatus === "ready" ||
        !inventory?.nextGrantExpiresAt ||
        !nextGrantCountdown
            ? inventoryGrantTimers
            : [
                  {
                      grantId: null,
                      expiresAt: inventory.nextGrantExpiresAt,
                      countdown: nextGrantCountdown,
                  },
              ];
    const canSettle = Boolean(
        inventory?.settlementEnabled && availableGrantCount > 0,
    );
    const areAllTargetsUnpickable =
        friendSteals.targets.length > 0 &&
        (!canSettle ||
            friendSteals.targets.every(
                (target) => countdownUntil(target.nextStealAt, now) !== null,
            ));
    const isSubmitting = friendSteals.submittingUserId !== null;
    const isDialogLocked = isSubmitting || isClaimAnimationRunning;

    useEffect(() => {
        if (isOpen) return;
        setCompletedSteal(null);
        setActionError(null);
        setIsClaimAnimationRunning(false);
    }, [isOpen]);

    useEffect(() => {
        const grants = inventoryGrantsRef.current;
        if (!grants) return;

        const updateOverflow = () => {
            const maxScrollLeft = Math.max(
                grants.scrollWidth - grants.clientWidth,
                0,
            );
            const nextOverflow = {
                left: grants.scrollLeft > 1,
                right: maxScrollLeft - grants.scrollLeft > 1,
            };

            setInventoryOverflow((currentOverflow) =>
                currentOverflow.left === nextOverflow.left &&
                currentOverflow.right === nextOverflow.right
                    ? currentOverflow
                    : nextOverflow,
            );
        };

        const animationFrame = window.requestAnimationFrame(updateOverflow);
        const resizeObserver = new ResizeObserver(updateOverflow);
        resizeObserver.observe(grants);
        Array.from(grants.children).forEach((child) => {
            resizeObserver.observe(child);
        });
        grants.addEventListener("scroll", updateOverflow, { passive: true });

        return () => {
            window.cancelAnimationFrame(animationFrame);
            resizeObserver.disconnect();
            grants.removeEventListener("scroll", updateOverflow);
        };
    }, [completedSteal, grantTimers.length, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            refreshedExpiryRef.current = null;
            return;
        }

        const expiresAt = inventory?.nextGrantExpiresAt;
        if (
            !expiresAt ||
            Date.parse(expiresAt) > now ||
            refreshedExpiryRef.current === expiresAt
        ) {
            return;
        }

        // The snapshot exposes only the earliest expiry. Refresh once when it
        // passes so a later grant (if present) becomes the next accurate timer.
        refreshedExpiryRef.current = expiresAt;
        void friendSteals.refresh();
    }, [friendSteals.refresh, inventory?.nextGrantExpiresAt, isOpen, now]);

    const requestClose = () => {
        if (!isDialogLocked) onClose();
    };

    const chooseTarget = async (target: ApiFriendStealTargetV2) => {
        haptics.impact("medium");
        setActionError(null);
        try {
            const response = await friendSteals.steal(target.userId);
            setCompletedSteal({ target, response });
            haptics.notification("success");
        } catch (error) {
            setActionError(
                error instanceof Error ? error.message : t("errors.generic"),
            );
            haptics.notification("error");
        }
    };

    const addFriend = () => {
        haptics.impact("medium");
        setActionError(null);
        void friendInviteSharing.share();
    };

    const addFriendViaTelegramLink = () => {
        haptics.impact("medium");
        setActionError(null);
        void friendInviteSharing.shareViaTelegramLink();
    };

    const addFriendOnClick = {
        shareMessage: addFriend,
        telegramShareLink: addFriendViaTelegramLink,
    } as const;

    const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const titleId = "friend-steal-dialog-title";
    const title = t("friendSteal.dialog.title");

    return (
        <SimpleDialog
            className="friend-steal-dialog"
            isOpen={isOpen}
            onClose={requestClose}
            closeOnBackdropClick={!isDialogLocked}
            aria-labelledby={titleId}
        >
            <section
                className="friend-steal-dialog__panel"
                data-view={completedSteal ? "result" : "targets"}
            >
                <h2 className="friend-steal-dialog__title" id={titleId}>
                    <span className="friend-steal-dialog__title-label">
                        {title}
                    </span>
                    <img
                        className="friend-steal-dialog__title-image"
                        src={getFriendStealTitleAsset(locale)}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                    />
                </h2>

                {completedSteal ? (
                    <FriendStealResultDialog
                        completedSteal={completedSteal}
                        locale={locale}
                        onClaimingChange={setIsClaimAnimationRunning}
                        onClose={onClose}
                    />
                ) : (
                    <div className="friend-steal-dialog__picker">
                        <div className="friend-steal-dialog__inventory">
                            <div className="friend-steal-dialog__inventory-heading">
                                <p>
                                    <Trans
                                        i18nKey={inventoryTitleKey}
                                        values={{
                                            count: availableGrantCount,
                                        }}
                                        components={{
                                            multiplier: <bdi dir="ltr" />,
                                        }}
                                    />
                                </p>
                            </div>

                            <div
                                className="friend-steal-dialog__inventory-grants"
                                ref={inventoryGrantsRef}
                                role="list"
                                aria-label={t("friendSteal.dialog.grantCount", {
                                    count: availableGrantCount,
                                })}
                                data-overflow-left={inventoryOverflow.left}
                                data-overflow-right={inventoryOverflow.right}
                                dir="ltr"
                            >
                                {grantTimers.length > 0 ? (
                                    grantTimers.map((grant, index) => (
                                        <span
                                            className={[
                                                "friend-steal-dialog__grant",
                                                index === 0
                                                    ? "is-selected"
                                                    : "",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                            role="listitem"
                                            aria-current={
                                                index === 0 ? "true" : undefined
                                            }
                                            key={
                                                grant.grantId ?? grant.expiresAt
                                            }
                                        >
                                            <img
                                                src={friendStealIcon}
                                                alt=""
                                                aria-hidden="true"
                                                draggable={false}
                                            />
                                            <span className="friend-steal-dialog__grant-timer">
                                                <time
                                                    dateTime={grant.expiresAt}
                                                >
                                                    {grant.countdown}
                                                </time>
                                            </span>
                                        </span>
                                    ))
                                ) : (
                                    <span
                                        className="friend-steal-dialog__inventory-empty"
                                        role="listitem"
                                    >
                                        {t("friendSteal.dialog.noGrants")}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="friend-steal-dialog__picker-heading">
                            <h3>{t("friendSteal.dialog.chooseTitle")}</h3>
                            <div
                                className="friend-steal-dialog__picker-heading-currencies"
                                aria-label={t(
                                    "friendSteal.dialog.supportedCurrencies",
                                )}
                            >
                                {Object.entries(STYLE_1_CURRENCY_ICONS).map(
                                    ([currencyCode, icon]) => (
                                        <img
                                            src={icon}
                                            alt={currencyCode}
                                            draggable={false}
                                            key={currencyCode}
                                        />
                                    ),
                                )}
                            </div>
                        </div>

                        {(actionError ?? friendInviteSharing.error) && (
                            <p
                                className="friend-steal-dialog__error"
                                role="alert"
                            >
                                {actionError ?? friendInviteSharing.error}
                            </p>
                        )}

                        <div
                            className="friend-steal-dialog__targets-panel"
                            data-state={friendSteals.loadStatus}
                        >
                            {(friendSteals.loadStatus === "loading" ||
                                (friendSteals.loadStatus === "idle" &&
                                    isAuthReady)) && (
                                <div
                                    className="friend-steal-dialog__targets-loading"
                                    role="status"
                                >
                                    <EmptyListArtwork />
                                    <span>
                                        {t("friendSteal.dialog.loading")}
                                    </span>
                                </div>
                            )}

                            {!isAuthReady && (
                                <div
                                    className="friend-steal-dialog__targets-status"
                                    role="status"
                                >
                                    {t("friendSteal.dialog.unavailable")}
                                </div>
                            )}

                            {friendSteals.loadStatus === "error" && (
                                <div
                                    className="friend-steal-dialog__targets-status"
                                    role="alert"
                                >
                                    <p>{friendSteals.loadError}</p>
                                    <SoundButton
                                        type="button"
                                        className="friend-steal-dialog__targets-status-retry"
                                        onClick={() => {
                                            haptics.impact("light");
                                            void friendSteals.refresh();
                                        }}
                                    >
                                        {t("friendSteal.dialog.retry")}
                                    </SoundButton>
                                </div>
                            )}

                            {friendSteals.loadStatus === "ready" && (
                                <ul className="friend-steal-dialog__targets">
                                    {friendSteals.targets.length === 0
                                        ? DUMMY_FRIEND_TARGETS.map((target) => {
                                              const displayName = `${target.firstName} ${target.lastName}`;

                                              return (
                                                  <li
                                                      key={target.id}
                                                      className="friend-steal-dialog__targets-dummy"
                                                      aria-hidden="true"
                                                  >
                                                      <ButtonVar1
                                                          className="friend-steal-dialog__target"
                                                          tabIndex={-1}
                                                          colors={
                                                              AVAILABLE_TARGET_COLORS
                                                          }
                                                          content={
                                                              <div className="friend-steal-dialog__target-content">
                                                                  <div className="friend-steal-dialog__target-content__left">
                                                                      <Avatar
                                                                          avatarUrl={
                                                                              target.avatarUrl
                                                                          }
                                                                          displayName={
                                                                              displayName
                                                                          }
                                                                          firstName={
                                                                              target.firstName
                                                                          }
                                                                          lastName={
                                                                              target.lastName
                                                                          }
                                                                      />
                                                                      <span
                                                                          className="friend-steal-dialog__target-name"
                                                                          data-layout="stacked"
                                                                      >
                                                                          <span>
                                                                              {
                                                                                  target.firstName
                                                                              }
                                                                          </span>
                                                                          <span>
                                                                              {
                                                                                  target.lastName
                                                                              }
                                                                          </span>
                                                                      </span>
                                                                  </div>
                                                                  <span className="friend-steal-dialog__target-action">
                                                                      <span className="friend-steal-dialog__target-choose">
                                                                          {t(
                                                                              "friendSteal.dialog.choose",
                                                                          )}
                                                                      </span>
                                                                  </span>
                                                              </div>
                                                          }
                                                      />
                                                  </li>
                                              );
                                          })
                                        : friendSteals.targets.map((target) => {
                                              const cooldown = countdownUntil(
                                                  target.nextStealAt,
                                                  now,
                                              );
                                              const robbedYou =
                                                  BigInt(
                                                      target.robbedYouCount,
                                                  ) > 0n;
                                              const isCurrentTarget =
                                                  friendSteals.submittingUserId ===
                                                  target.userId;
                                              const disabled =
                                                  !canSettle ||
                                                  cooldown !== null ||
                                                  isSubmitting;
                                              const cryptoEligibilityMessageKey =
                                                  targetCryptoEligibilityKey(
                                                      target.cryptoEligibility,
                                                  );
                                              const cryptoEligibilityMessage =
                                                  cryptoEligibilityMessageKey
                                                      ? t(
                                                            cryptoEligibilityMessageKey,
                                                            {
                                                                name: target.displayName,
                                                            },
                                                        )
                                                      : null;

                                              return (
                                                  <li key={target.userId}>
                                                      <ButtonVar1
                                                          className="friend-steal-dialog__target"
                                                          data-cooldown={
                                                              cooldown !== null
                                                          }
                                                          data-crypto-eligibility={
                                                              target.cryptoEligibility
                                                          }
                                                          disabled={disabled}
                                                          colors={
                                                              AVAILABLE_TARGET_COLORS
                                                          }
                                                          aria-label={[
                                                              t(
                                                                  "friendSteal.dialog.chooseFriend",
                                                                  {
                                                                      name: target.displayName,
                                                                  },
                                                              ),
                                                              cryptoEligibilityMessage,
                                                          ]
                                                              .filter(Boolean)
                                                              .join(". ")}
                                                          // TODO: TEMP!
                                                          onClick={() =>
                                                              void chooseTarget(
                                                                  target,
                                                              )
                                                          }
                                                          content={
                                                              <div className="friend-steal-dialog__target-content">
                                                                  <div className="friend-steal-dialog__target-content__left">
                                                                      <Avatar
                                                                          avatarUrl={
                                                                              target.avatarUrl
                                                                          }
                                                                          displayName={
                                                                              target.displayName
                                                                          }
                                                                          firstName={
                                                                              target.firstName
                                                                          }
                                                                          lastName={
                                                                              target.lastName
                                                                          }
                                                                      />
                                                                      <span
                                                                          className="friend-steal-dialog__target-name"
                                                                          data-layout={
                                                                              target.firstName &&
                                                                              target.lastName
                                                                                  ? "stacked"
                                                                                  : "wrap"
                                                                          }
                                                                      >
                                                                          {target.firstName ||
                                                                          target.lastName ? (
                                                                              <>
                                                                                  {target.firstName && (
                                                                                      <span>
                                                                                          {
                                                                                              target.firstName
                                                                                          }
                                                                                      </span>
                                                                                  )}
                                                                                  {target.lastName && (
                                                                                      <span>
                                                                                          {
                                                                                              target.lastName
                                                                                          }
                                                                                      </span>
                                                                                  )}
                                                                              </>
                                                                          ) : (
                                                                              <span>
                                                                                  {
                                                                                      target.displayName
                                                                                  }
                                                                              </span>
                                                                          )}
                                                                      </span>
                                                                  </div>
                                                                  <span className="friend-steal-dialog__target-action">
                                                                      <span
                                                                          className="friend-steal-dialog__target-history"
                                                                          data-robbed={
                                                                              robbedYou
                                                                          }
                                                                          dir={
                                                                              robbedYou
                                                                                  ? "ltr"
                                                                                  : undefined
                                                                          }
                                                                      >
                                                                          {cooldown ? (
                                                                              <time
                                                                                  dateTime={
                                                                                      target.nextStealAt ??
                                                                                      undefined
                                                                                  }
                                                                              >
                                                                                  {
                                                                                      cooldown
                                                                                  }
                                                                              </time>
                                                                          ) : robbedYou ? (
                                                                              t(
                                                                                  "friendSteal.dialog.robbedYou",
                                                                                  {
                                                                                      count: target.robbedYouCount,
                                                                                  },
                                                                              )
                                                                          ) : (
                                                                              t(
                                                                                  "friendSteal.dialog.neverRobbedYou",
                                                                              )
                                                                          )}
                                                                      </span>
                                                                      {/*
                                                                      {cryptoEligibilityMessage && (
                                                                          <span className="friend-steal-dialog__target-crypto-limit">
                                                                              {
                                                                                  cryptoEligibilityMessage
                                                                              }
                                                                          </span>
                                                                      )}
                                                                    */}
                                                                      <span className="friend-steal-dialog__target-choose">
                                                                          {isCurrentTarget
                                                                              ? t(
                                                                                    "friendSteal.dialog.stealing",
                                                                                )
                                                                              : t(
                                                                                    "friendSteal.dialog.choose",
                                                                                )}
                                                                      </span>
                                                                  </span>
                                                              </div>
                                                          }
                                                      />
                                                  </li>
                                              );
                                          })}
                                    {friendSteals.targets.length > 0 && (
                                        <li
                                            className={[
                                                "friend-steal-dialog__targets-invite",
                                                areAllTargetsUnpickable &&
                                                    "is-all-targets-unpickable",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                        >
                                            {/* Legacy handler: onClick={addFriendOnClick.shareMessage} */}
                                            <ButtonVar1
                                                className="friend-steal-dialog__target friend-steal-dialog__targets-invite-button"
                                                colors={
                                                    areAllTargetsUnpickable
                                                        ? ALL_TARGETS_UNPICKABLE_ADD_FRIEND_COLORS
                                                        : ADD_FRIEND_COLORS
                                                }
                                                onClick={
                                                    addFriendOnClick.telegramShareLink
                                                }
                                                aria-label={t(
                                                    "friendSteal.dialog.addFriend",
                                                )}
                                                content={
                                                    <div className="friend-steal-dialog__targets-invite-content">
                                                        {areAllTargetsUnpickable && (
                                                            <ShineReflectionDot
                                                                className="friend-steal-dialog__targets-invite-shine"
                                                                aria-hidden="true"
                                                                focusable="false"
                                                            />
                                                        )}
                                                        <img
                                                            src={
                                                                areAllTargetsUnpickable
                                                                    ? Green3DPlusIcon
                                                                    : friendIcon
                                                            }
                                                            alt=""
                                                            aria-hidden="true"
                                                            draggable={false}
                                                        />
                                                        <span>
                                                            {t(
                                                                "friendSteal.dialog.addFriend",
                                                            )}
                                                        </span>
                                                    </div>
                                                }
                                            />
                                        </li>
                                    )}
                                </ul>
                            )}

                            {friendSteals.loadStatus === "ready" &&
                                friendSteals.targets.length === 0 && (
                                    <>
                                        <div
                                            className="friend-steal-dialog__targets-friendless-overlay"
                                            aria-hidden="true"
                                        />
                                        <div className="friend-steal-dialog__targets-friendless-cta">
                                            {/* Legacy handler: onClick={addFriendOnClick.shareMessage} */}
                                            <ButtonVar1
                                                className="friend-steal-dialog__targets-friendless-add"
                                                colors={
                                                    FRIENDLESS_ADD_FRIEND_COLORS
                                                }
                                                onClick={
                                                    addFriendOnClick.telegramShareLink
                                                }
                                                aria-label={t(
                                                    "friendSteal.dialog.addFriend",
                                                )}
                                                content={
                                                    <span className="friend-steal-dialog__targets-friendless-add-label">
                                                        <img
                                                            src={
                                                                Green3DPlusIcon
                                                            }
                                                            alt=""
                                                            aria-hidden="true"
                                                            draggable={false}
                                                        />
                                                        {t(
                                                            "friendSteal.dialog.addFriend",
                                                        )}
                                                    </span>
                                                }
                                            />
                                            <p className="friend-steal-dialog__targets-friendless-cta__text">
                                                {t(
                                                    "friendSteal.dialog.noFriends",
                                                )}
                                            </p>
                                        </div>
                                    </>
                                )}
                        </div>
                    </div>
                )}
            </section>
        </SimpleDialog>
    );
}
